import { type FC, type PropsWithChildren, useEffect, useState } from 'react';
import { useCurrentUser } from '../../auth/currentUserContext';
import { Error as ErrorPage } from '../../components/admin/error';
import { Loading } from '../../components/admin/loading';
import { useSendbird } from '@sendbird/uikit-react';
import { ConnectionHandler } from '@sendbird/chat';

export type ConnectionState =
  | 'CONNECTED'
  | 'DISCONNECTED'
  | 'RECONNECT_STARTED'
  | 'UNKNOWN';

export const SendbirdConnectionManager: FC<PropsWithChildren> = ({
  children,
}) => {
  const user = useCurrentUser();
  const {
    state: { stores },
    actions,
  } = useSendbird();
  const sdk = stores.sdkStore.sdk;

  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionState>('UNKNOWN');

  useEffect(() => {
    if (!sdk) return;

    const handlerId = crypto.randomUUID();

    try {
      const handler = new ConnectionHandler({
        onConnected() {
          setConnectionStatus('CONNECTED');
        },
        onDisconnected() {
          setConnectionStatus('DISCONNECTED');
        },
        onReconnectStarted() {
          setConnectionStatus('RECONNECT_STARTED');
        },
        onReconnectSucceeded() {
          setConnectionStatus('CONNECTED');
        },
        onReconnectFailed() {
          setConnectionStatus('DISCONNECTED');
        },
      });

      if (typeof sdk.addConnectionHandler === 'function') {
        sdk.addConnectionHandler(handlerId, handler);
        // Set initial state based on current connection status if available
        if (typeof sdk.getConnectionState === 'function') {
          if (sdk.getConnectionState() === 'OPEN') {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setConnectionStatus('CONNECTED');
          } else {
            setConnectionStatus('DISCONNECTED');
          }
        }
      }
    } catch (error) {
      console.error(
        'Something went wrong with the connectionHandler registration:',
        error
      );
      setConnectionStatus('UNKNOWN');
    }

    return () => {
      try {
        if (typeof sdk.removeConnectionHandler === 'function') {
          sdk.removeConnectionHandler(handlerId);
        }
      } catch (error) {
        console.error('Failed to remove the connectionHandler:', error);
      }
    };
  }, [sdk]);

  if (connectionStatus === 'DISCONNECTED') {
    return (
      <ErrorPage
        error={new Error('Connection to chat lost')}
        resetErrorBoundary={() => void actions.connect(user.id)}
      />
    );
  }

  // For some reason, the `ConnectionHandler` is not executed immediately, so we can't rely on it to be the source of truth for the connection state during the 1st connection.
  // Instead, we check the user store to see if it has been initialized, which indicates that, at least, Sendbird has been initialized.
  if (!stores.userStore.initialized) {
    return <Loading />;
  }

  return children;
};
